import React, {
  useCallback, useLayoutEffect, useRef, useState,
} from 'react';
import { flushSync } from 'react-dom';
import {
  alpha, Box, ButtonBase, Stack, Theme, Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import pingwinMark from '../../assets/logos/pingwin-mark.png';
import focusRing from '../../theme/focus';
import {
  StartingPromptDraw,
  startingPromptCountThatFits,
  startingPromptsToShow,
} from './startingPrompts';

const SMALL_MARK_SIZE = 32;
// Theme spacing units, shared by the visible prompts and the hidden ones measured beside them.
const SIDE_PADDING = 4;

// The theme gives body1's font size as a bare number of pixels and its line height as a
// factor. In a CSS calc() their product has no unit and cannot be taken from 32px, so the
// browser would drop the margin. The first line's height is worked out in px here instead.
const toPx = (value: string | number | undefined, remPx: number) => {
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value ?? '');
  return value?.trim().endsWith('rem') ? parsed * remPx : parsed;
};

const firstLineHeightPx = (theme: Theme) => {
  const { body1, htmlFontSize = 16 } = theme.typography;
  const fontSize = toPx(body1.fontSize, htmlFontSize);
  const { lineHeight } = body1;
  if (typeof lineHeight === 'string' && /(px|rem)\s*$/.test(lineHeight)) {
    return toPx(lineHeight, htmlFontSize);
  }
  return (parseFloat(String(lineHeight ?? 1.5)) || 1.5) * fontSize;
};

const INTRO = 'Ask about cases, rules and entities. Pingwin sees only what you have access to.';

const verticalPadding = (element: HTMLElement) => {
  const { paddingTop, paddingBottom } = getComputedStyle(element);
  return (parseFloat(paddingTop) || 0) + (parseFloat(paddingBottom) || 0);
};

const PinguStartingPrompts = ({
  draw,
  onSelect,
  showMark = false,
  transcript = null,
}: {
  draw: StartingPromptDraw;
  onSelect: (prompt: string) => void;
  // Drawn when the large mark in the transcript doesn't fit, so the empty state keeps one mark.
  showMark?: boolean;
  // The empty transcript above, the only part of the panel that gives up height. Whatever
  // it has beyond its own padding is room the prompts could take without clipping anything.
  transcript?: HTMLElement | null;
}) => {
  const [count, setCount] = useState(draw.prompts.length);
  const stackRef = useRef<HTMLDivElement>(null);
  const promptRefs = useRef(new Map<string, HTMLElement>());
  const shown = startingPromptsToShow(draw, count);

  const countRef = useRef(count);
  countRef.current = count;

  // Prompts that don't show are still laid out, out of the flow and invisible, so the height
  // each would take is known before it is let back in. The DOM is read rather than a
  // reported height, as a transcript already squeezed to its padding doesn't change size.
  // The spare room counts the shown prompts too, so it is the same whatever the count is.
  const fit = useCallback(() => {
    const stack = stackRef.current;
    if (!transcript || !stack) return;

    const gap = parseFloat(getComputedStyle(stack).rowGap) || 0;
    const heights = Object.fromEntries(draw.prompts.map((prompt) => [
      prompt,
      promptRefs.current.get(prompt)?.getBoundingClientRect().height ?? 0,
    ]));
    const shownSpace = startingPromptsToShow(draw, countRef.current)
      .reduce((total, prompt) => total + heights[prompt] + gap, 0);
    const spare = transcript.clientHeight - verticalPadding(transcript);
    setCount(startingPromptCountThatFits(draw, heights, gap, spare + shownSpace));
  }, [draw, transcript]);

  useLayoutEffect(fit, [count, fit]);

  // The count follows the column the transcript sits in, and the column's other parts such
  // as the composer, but not the transcript or these prompts. A count change resizes those
  // two, and resizing an element inside its own observer's delivery is a ResizeObserver
  // loop. A change in the column's width re-wraps the prompts, which the transcript's
  // height alone doesn't show. Applied with flushSync so the count is right before paint.
  useLayoutEffect(() => {
    const column = transcript?.parentElement;
    const stack = stackRef.current;
    if (!column || !stack || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => flushSync(fit));
    observer.observe(column);
    Array.from(column.children)
      .filter((child) => child !== transcript && child !== stack)
      .forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [fit, transcript]);

  return (
    <Stack
      ref={stackRef}
      spacing={2}
      useFlexGap
      sx={{ position: 'relative', px: SIDE_PADDING, pb: 1 }}
    >
      {showMark ? (
        <Stack direction="row" spacing={3} alignItems="flex-start" sx={{ mb: 1 }}>
          <Box
            component="img"
            src={pingwinMark}
            alt=""
            sx={(theme) => {
              // Centred on the intro's first line, and overhanging it rather than making
              // the row taller, so the transcript keeps the same height with either mark.
              const overhang = (firstLineHeightPx(theme) - SMALL_MARK_SIZE) / 2;
              return {
                width: SMALL_MARK_SIZE,
                height: SMALL_MARK_SIZE,
                flexShrink: 0,
                my: `${overhang}px`,
              };
            }}
          />
          <Typography variant="body1" color="text.secondary">
            {INTRO}
          </Typography>
        </Stack>
      ) : (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          {INTRO}
        </Typography>
      )}
      {draw.prompts.map((prompt) => {
        const hidden = !shown.includes(prompt);
        return (
          <ButtonBase
            key={prompt}
            ref={(element: HTMLButtonElement | null) => {
              if (element) promptRefs.current.set(prompt, element);
              else promptRefs.current.delete(prompt);
            }}
            onClick={() => onSelect(prompt)}
            aria-hidden={hidden || undefined}
            tabIndex={hidden ? -1 : undefined}
            sx={{
              justifyContent: 'flex-start',
              gap: 2,
              width: '100%',
              px: 3,
              py: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.5,
              textAlign: 'left',
              color: 'text.primary',
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.emphasisText.main, 0.08),
              },
              '&:focus-visible': { boxShadow: focusRing },
              ...(hidden && {
                position: 'absolute',
                top: 0,
                left: (theme: Theme) => theme.spacing(SIDE_PADDING),
                right: (theme: Theme) => theme.spacing(SIDE_PADDING),
                width: 'auto',
                visibility: 'hidden',
              }),
            }}
          >
            <ArrowForwardIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body1">{prompt}</Typography>
          </ButtonBase>
        );
      })}
    </Stack>
  );
};

export default PinguStartingPrompts;
